import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useQuota } from "../../hooks/useQuota";
import { QuotaBar } from "../ui/QuotaBar";

const NAV_ITEMS = [
  {
    to: "/app",
    label: "My Files",
    end: true,
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
      </svg>
    ),
  },
  {
    to: "/app/trash",
    label: "Trash",
    end: false,
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const { usedStr, quotaStr } = useQuota();
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/contact")
      .then((r) => r.json())
      .then((d) => setAdminEmail(d.adminEmail ?? null))
      .catch(() => {});
  }, []);

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-30 flex h-full w-64 flex-col border-r border-gray-800 bg-gray-900
        transform transition-transform duration-200 ease-in-out
        md:static md:z-auto md:w-56 md:translate-x-0
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}
    >
      {/* Logo */}
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
            <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H5.5z" />
            </svg>
          </div>
          <span className="text-lg font-bold text-white">TitanCloud</span>
        </div>
        {/* Close button — mobile only */}
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200 md:hidden"
          aria-label="Close menu"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2 pt-4">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-brand-700 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}

        {user?.role === "ADMIN" && (
          <NavLink
            to="/app/admin"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-brand-700 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              }`
            }
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a5 5 0 100 10A5 5 0 0010 2zM0 16.72a1 1 0 01.275-.707A9.006 9.006 0 0110 14a9.006 9.006 0 019.725 2.013 1 1 0 01-1.45 1.38A7.006 7.006 0 0010 16a7.006 7.006 0 00-8.275 1.393A1 1 0 010 16.72z" />
            </svg>
            Admin
          </NavLink>
        )}
      </nav>

      {/* Quota */}
      <div className="border-t border-gray-800">
        <QuotaBar />
      </div>

      {/* Get Extra Storage */}
      {adminEmail && user?.role !== "ADMIN" && (
        <div className="border-t border-gray-800 px-3 py-2">
          <a
            href={`mailto:${adminEmail}?subject=${encodeURIComponent("TitanCloud - Storage Increase Request")}&body=${encodeURIComponent(`Hi,\n\nI'm currently using ${usedStr} of my ${quotaStr} storage quota on TitanCloud.\n\nCould you please increase my storage allocation?\n\nThank you,\n${user?.displayName ?? ""}`)}`}
            className="flex items-center justify-center gap-2 rounded-lg bg-brand-600/20 px-3 py-2 text-xs font-medium text-brand-400 transition-colors hover:bg-brand-600/30"
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            Get Extra Storage
          </a>
        </div>
      )}

      {/* User info */}
      {user && (
        <div className="border-t border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="h-7 w-7 rounded-full"
              />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-xs text-white">
                {user.displayName[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-200">
                {user.displayName}
              </p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
